<template>
  <div
      ref="popupRoot"
      @pointerup.capture="guardMobilePointerRelease"
      @touchend.capture="guardMobilePointerRelease"
      @mouseup.capture="guardMobilePointerRelease"
      @click.capture="guardMobilePointerRelease"
  >
    <div
        v-if="showAutocompletePopup"
        class="q-autocomplete-mask"
        @pointerdown.stop="popupStore.hideAutocomplete"
        :class="{'q-autocomplete-mask--interactive': isMobileView}"
    ></div>
    <AutocompleteInputContainer/>
    <div v-show="showAutocompletePopup"
         id="smartsearch-autocomplete__popup-container"
         class="smartsearch-autocomplete__popup-container q-shadow q-p-2 q-bg-body q-rounded q-bg-white"
         :class="{'smartsearch-autocomplete__popup-container--bridge': isBridgeActive}"
         :style="bridgePopupStyle"
    >
      <OverlayLoader :loading="loading"/>
      <LinearLoader :loading="loading"/>
      <AutocompleteContent/>
    </div>
  </div>

</template>

<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, toRefs, watch} from 'vue';
import {storeToRefs} from 'pinia';
import {usePopupStore} from "@/store/AutocompletePopup/PopupStore";

import AutocompleteInputContainer from '@/components/AutocompletePopup/AutocompleteInputContainer.vue';
import LinearLoader from '@/components/LinearLoader.vue';
import OverlayLoader from '@/components/OverlayLoader.vue';
import AutocompleteContent from '@/components/AutocompletePopup/AutocompleteContainer.vue';
import {useMobileViewStore} from "@/store/MobileViewStore";
import {useBridgeStore} from "@/store/BridgeStore";

const popupStore = usePopupStore()
const {showAutocompletePopup, loading} = toRefs(popupStore)
const mobileViewStore = useMobileViewStore();
const bridgeStore = useBridgeStore()
const { isMobileView } = storeToRefs(mobileViewStore)
const { forceBodyPlacement } = storeToRefs(bridgeStore)
const popupRoot = ref<HTMLElement | null>(null)
const pointerReleaseGuardActive = ref(false)
let pointerReleaseGuardTimer: ReturnType<typeof setTimeout> | null = null
let unregisterPopupSafe: (() => void) | null = null

const deactivatePointerReleaseGuard = () => {
  pointerReleaseGuardActive.value = false
  if (pointerReleaseGuardTimer) {
    clearTimeout(pointerReleaseGuardTimer)
    pointerReleaseGuardTimer = null
  }
}

const activatePointerReleaseGuard = () => {
  if (!isMobileView.value) {
    return
  }
  pointerReleaseGuardActive.value = true
  if (pointerReleaseGuardTimer) {
    clearTimeout(pointerReleaseGuardTimer)
  }
  pointerReleaseGuardTimer = setTimeout(() => {
    pointerReleaseGuardActive.value = false
    pointerReleaseGuardTimer = null
  }, 250)
}

const guardMobilePointerRelease = (event: PointerEvent | MouseEvent | TouchEvent) => {
  if (!pointerReleaseGuardActive.value) {
    return
  }
  event.stopImmediatePropagation()
  event.stopPropagation()
  event.preventDefault()
  deactivatePointerReleaseGuard()
}

const syncPopupSafeElement = () => {
  unregisterPopupSafe?.()
  if (popupRoot.value) {
    unregisterPopupSafe = popupStore.registerSafeElement(popupRoot.value)
  }
}

watch(popupRoot, () => {
  syncPopupSafeElement()
})

watch(showAutocompletePopup, (isOpen, wasOpen) => {
  if (isOpen && !wasOpen) {
    activatePointerReleaseGuard()
  }
})

const isBridgeActive = computed(() => bridgeStore.hasClientInput && !isMobileView.value)
const useBridgePlacement = computed(() => isBridgeActive.value && !forceBodyPlacement.value)

const bridgePopupStyle = computed(() => {
  if (!useBridgePlacement.value || !bridgeStore.popupPlacement) {
    return {}
  }

  const { left, top, width, arrowOffset } = bridgeStore.popupPlacement

  return {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    maxWidth: `${width}px`,
    minWidth: `${width}px`,
    transform: 'none',
    '--bridge-arrow-left': `${arrowOffset}px`
  }
})

const updateBridgePlacement = () => {
  if (!useBridgePlacement.value) {
    return
  }
  bridgeStore.updatePopupPlacement()
}

let bridgeListenersAttached = false

const attachBridgeListeners = () => {
  if (bridgeListenersAttached) {
    return
  }
  window.addEventListener('resize', updateBridgePlacement)
  window.addEventListener('scroll', updateBridgePlacement, true)
  bridgeListenersAttached = true
}

const detachBridgeListeners = () => {
  if (!bridgeListenersAttached) {
    return
  }
  window.removeEventListener('resize', updateBridgePlacement)
  window.removeEventListener('scroll', updateBridgePlacement, true)
  bridgeListenersAttached = false
}

watch([showAutocompletePopup, isMobileView], ([isOpen, mobile]) => {
  if (isOpen && !mobile && useBridgePlacement.value) {
    updateBridgePlacement()
    attachBridgeListeners()
  } else {
    detachBridgeListeners()
  }
})

watch(isBridgeActive, (active) => {
  if (active && showAutocompletePopup.value && !forceBodyPlacement.value) {
    updateBridgePlacement()
    attachBridgeListeners()
  } else if (!active) {
    detachBridgeListeners()
  }
})

watch(forceBodyPlacement, (forced) => {
  if (forced) {
    detachBridgeListeners()
  } else if (showAutocompletePopup.value && isBridgeActive.value) {
    updateBridgePlacement()
    attachBridgeListeners()
  }
})

watch(
  () => bridgeStore.clientInput,
  () => {
    if (showAutocompletePopup.value) {
      updateBridgePlacement()
    }
  }
)


onUnmounted(() => {
  detachBridgeListeners()
  window.removeEventListener('resize', mobileViewStore.updateIsMobileView);
  unregisterPopupSafe?.()
  deactivatePointerReleaseGuard()
});

onMounted(() => {
  syncPopupSafeElement()
  window.addEventListener('resize', mobileViewStore.updateIsMobileView);
});
</script>

<style>


/*@import "bootstrap/dist/css/bootstrap.min.css";*/
#smartsearch-autocomplete {
  font-size: 16px;
}

.q-overlay {
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.4);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 5%;
}

.q-autocomplete-mask {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0);
  z-index: 998;
  pointer-events: auto;
}
.q-autocomplete-mask.q-autocomplete-mask--interactive {
  background: rgba(15, 23, 42, 0);
  z-index: 999998;
}


.smartsearch-autocomplete__popup-container:before {
  position: absolute;
  content: '';
  width: 20px;
  height: 20px;
  background: white;
  display: block;
  z-index: -1;
  left: 50%;
  top: -6px;
  transform: rotate(45deg);
}

.smartsearch-autocomplete__popup-container {
  position: absolute;
  min-width: 960px;
  max-width: 960px;
  background: #fff;
  color: #343a40;
  z-index: 10000;
  min-height: 500px;
  left: 50%;
  transform: translate(-50%, 0);
}

.smartsearch-autocomplete__popup-container--bridge {
  min-width: 0;
  max-width: none;
  position: fixed;
}

.smartsearch-autocomplete__popup-container--bridge:before {
  left: var(--bridge-arrow-left, 48px);
}


@media (max-width: 991.98px) {
  .smartsearch-autocomplete__popup-container {
    max-width: 100vw;
    min-width: unset !important;
    height: calc(100dvh - 60px);
    overflow-y: scroll;
    overflow-x: hidden;
    left: unset;
    transform: unset;
  }

  .q-autocomplete-active {
    overflow: hidden;
    position: fixed;
    width: 100%;
    height: 100%;
  }

  .q-autocomplete-active #smartsearch-autocomplete {
    position: fixed;
    background: #f8f9fa;
    top: 0;
    left: 0;
    z-index: 1080;
    right: 0;
    bottom: 0;
    padding-top: 15px;
    max-width: 100vw;
  }
}


@media (min-width: 992px) and (max-width: 1080px) {
  .smartsearch-autocomplete__popup-container {
    position: absolute;
    min-width: 100vw;
    max-width: 100vw;
    width: 100vw;
    background: #fff;
    color: #343a40;
    z-index: 10000;
    min-height: 500px;
    left: 50%;
    transform: translate(-50%, 0);
  }

}


/* ============ MOBILE ONLY ============ */
@media (max-width: 991.98px) {

  /* Lock scrolla dokumentu, gdy aktywne autocomplete */
  .q-autocomplete-active {
    overflow: hidden !important;
    position: fixed !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    overscroll-behavior: none !important;
    touch-action: none; /* mniej bounce’u */
  }

  /* Root kontenera widgetu – pełnoekranowy layer */
  .q-autocomplete-active #smartsearch-autocomplete {
    position: fixed !important;
    inset: 0 !important;
    z-index: 999999 !important;
    max-width: 100vw !important;

    /* Fallbacky kolejno: 100vh -> 100dvh/svh -> JS zmienna */
    height: 100vh;
    height: 100dvh;
    height: 100svh;
    height: calc(var(--app-vh, 1vh) * 100);

    background: #f8f9fa;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    overscroll-behavior: contain;
  }

  /* Sam popup z listą – pełna szerokość, fixed i przewijalny środek */
  .q-autocomplete-active .smartsearch-autocomplete__popup-container {
    position: fixed !important;
    inset: auto 0 0 0 !important; /* nie zasłania top bara */
    top: 60px !important; /* wysokość paska z inputem; dopasuj jeżeli się różni */
    z-index: 9999999 !important;
    width: 100vw !important;

    /* Fallbacky wysokości: 100% ekranu minus top bar i safe area */
    height: calc(100vh - 60px) !important;
    height: calc(100svh - 60px) !important;
    height: calc(100dvh - 60px) !important;

    background: #fff !important;
    min-height: 0 !important; /* ważne przy flex/grid */
    overflow-y: auto !important;
    overflow-x: hidden !important;
    -webkit-overflow-scrolling: touch;
    box-shadow: 0 -1px 0 rgba(0, 0, 0, .06);
  }

  /* Strzałka + input – sticky top bar (jak Gmail) */
  .q-autocomplete-active .autocomplete-popup__input-container-group {
    position: fixed !important;
    top: 0!important;
    left: 0!important;
    right: 0!important;
    z-index: 9999999!important;
    display: grid!important;
    grid-template-columns: auto 1fr!important;
    align-items: center!important;
    column-gap: 8px!important;
    padding: max(6px, env(safe-area-inset-top)) 10px 6px 10px!important;
    background: #f2f3f5!important;
  }

  /* Usuń mobilnie „trójkąt” wskazujący input (niepotrzebny w trybie full-screen) */
  .q-autocomplete-active .smartsearch-autocomplete__popup-container:before {
    display: none !important;
  }
}


:where(body, html)[data-qonizer-autocomplete-body-placement="true"].q-autocomplete-active {
  overflow: hidden !important;
  position: fixed !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  overscroll-behavior: none !important;
  touch-action: none;
}

.q-autocomplete-active #smartsearch-autocomplete[data-qonizer-autocomplete-body-placement="true"] {
  position: fixed !important;
  inset: 0 !important;
  z-index: 999999 !important;
  max-width: 100vw !important;
  height: 100vh;
  height: 100dvh;
  height: 100svh;
  height: calc(var(--app-vh, 1vh) * 100);
  background: #f8f9fa;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  overscroll-behavior: contain;
}

.q-autocomplete-active #smartsearch-autocomplete[data-qonizer-autocomplete-body-placement="true"] .smartsearch-autocomplete__popup-container {
  position: fixed !important;
  inset: auto 0 0 0 !important;
  top: 60px !important;
  z-index: 9999999 !important;
  width: 100vw !important;
  height: calc(100vh - 60px) !important;
  height: calc(100svh - 60px) !important;
  height: calc(100dvh - 60px) !important;
  background: #fff !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 -1px 0 rgba(0, 0, 0, .06);
}

.q-autocomplete-active #smartsearch-autocomplete[data-qonizer-autocomplete-body-placement="true"] .autocomplete-popup__input-container-group {
  position: fixed !important;
  top: 0!important;
  left: 0!important;
  right: 0!important;
  z-index: 9999999!important;
  display: grid!important;
  grid-template-columns: auto 1fr!important;
  align-items: center!important;
  column-gap: 8px!important;
  padding: max(6px, env(safe-area-inset-top)) 10px 6px 10px!important;
  background: #f2f3f5!important;
}

.q-autocomplete-active #smartsearch-autocomplete[data-qonizer-autocomplete-body-placement="true"] .smartsearch-autocomplete__popup-container:before {
  display: none !important;
}


</style>
